import { useCallback, useMemo } from 'react';

import { DropdownListItem } from 'webclient.constants';
import { getSelectedOptions } from './dropdown-multiselect.utils';
import { DropdownItemCheckbox } from './dropdown-item';
import { Dropdown } from './dropdown';
import {
  DropdownSelectItem,
  DropdownSelectItemClear,
} from './dropdown-item.style';
import { MarginProps } from 'approot/shared/styles/spacing.style';

type Props = {
  options: DropdownListItem[];
  label: string;
  dataSelector: string;
  selectedOptions?: string[];
  handleChangeCheckbox: (values: string[]) => void;
  menuWidth?: number;
  menuMatchToggleWidth?: boolean;
  hasError?: boolean;
  disabled?: boolean;
} & MarginProps;

const DropdownMultiselectAllToggle = ({
  options,
  label,
  dataSelector,
  selectedOptions,
  handleChangeCheckbox,
  menuWidth,
  menuMatchToggleWidth = true,
  hasError,
  disabled,
}: Props) => {
  const dropdownOptions = useMemo(() => options, [options]);
  const selectedValues = useMemo(
    () => getSelectedOptions(selectedOptions),
    [selectedOptions]
  );

  const hasAllSelected = useMemo(() => {
    if (options.length === 0) return false;
    return options.every(opt => selectedValues?.includes(opt.value));
  }, [options, selectedValues]);
  const handleAll = useCallback(() => {
    if (hasAllSelected) {
      handleChangeCheckbox([]);
    } else {
      handleChangeCheckbox(options.map(o => o.value));
    }
  }, [hasAllSelected, options, handleChangeCheckbox]);

  return (
    <Dropdown
      menuMatchToggleWidth={menuMatchToggleWidth}
      closeMenuOnSelect={false}
      disabled={disabled}
      hasError={hasError}
      data-selector={`${dataSelector}`}
      menuWidth={menuWidth}
      buttonElement={label}
      dropdownContents={
        <>
          <DropdownSelectItem
            data-selector={`${dataSelector}-all-toggle`}
            data-dropdown-item="true"
            onClick={handleAll}
          >
            <DropdownSelectItemClear
              data-selector={`${dataSelector}-all-toggle-option`}
            >
              {hasAllSelected ? 'Deselect All' : 'Select All'}
            </DropdownSelectItemClear>
          </DropdownSelectItem>

          {dropdownOptions.map(option => (
            <DropdownItemCheckbox
              key={option.value}
              data-selector={`${dataSelector}-option`}
              option={option}
              checked={selectedValues?.includes(option.value) || false}
              handleSelect={() => {
                const next = (selectedValues || []).includes(option.value)
                  ? (selectedValues || []).filter(v => v !== option.value)
                  : ([...(selectedValues || []), option.value] as string[]);

                handleChangeCheckbox(next);
              }}
            />
          ))}
        </>
      }
    />
  );
};

export default DropdownMultiselectAllToggle;
